import { listing as listingSchema, searchResult as searchResultSchema, type Listing, type ListingCard, type SearchResult } from '@solote/contracts';
import { platformApiUrl } from '@/core/config/env';
import { request } from '@/core/http/client';

/**
 * Acesso ao catalogo. Os filtros sao serializados a partir das CHAVES
 * configuradas em `search.filters` — o cliente nao conhece nome de coluna,
 * so a chave que o painel definiu.
 *
 * A VITRINE ja fala com a API real (`services/platform`) quando
 * `VITE_PLATFORM_API_URL` esta definida. O envio de lead continua no mock: o
 * CRM e outro contexto, ainda nao construido — e misturar os dois numa mesma
 * base esconderia qual metade e real.
 */

/** Base da vitrine: a API do acervo quando configurada, o mock caso contrario. */
const acervo = { baseUrl: platformApiUrl };

export type SearchParams = {
  q?: string | null;
  filters?: Record<string, unknown>;
  sort?: string | null;
  page?: number;
  pageSize?: number;
};

function serializeFilters(filters: Record<string, unknown> = {}): Record<string, string> {
  const query: Record<string, string> = {};

  for (const [key, value] of Object.entries(filters)) {
    if (value === null || value === undefined || value === '') continue;

    if (Array.isArray(value)) {
      if (value.length > 0) query[key] = value.join(',');
      continue;
    }
    if (typeof value === 'object') {
      // Intervalos viram <chave>_min / <chave>_max.
      const range = value as { min?: number | null; max?: number | null };
      if (range.min !== null && range.min !== undefined) query[`${key}_min`] = String(range.min);
      if (range.max !== null && range.max !== undefined) query[`${key}_max`] = String(range.max);
      continue;
    }
    query[key] = String(value);
  }

  return query;
}

export async function searchListings(params: SearchParams): Promise<SearchResult> {
  const resposta = await request<unknown>('/listings', {
    ...acervo,
    query: {
      q: params.q ?? undefined,
      sort: params.sort ?? undefined,
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 24,
      ...serializeFilters(params.filters),
    },
  });

  // Mesma razão do detalhe: o zod aplica os padrões e a divergência aparece na
  // fronteira, com nome de campo, em vez de virar tela branca na grade.
  return searchResultSchema.parse(resposta);
}

/**
 * VALIDA a resposta contra o contrato antes de devolvê-la.
 *
 * Não é formalidade: o zod aplica os PADRÕES declarados, e é isso que impede um
 * campo ausente de chegar como `undefined` no meio de um componente. Já custou
 * duas telas brancas neste projeto — `listing.badges is not iterable` e
 * `Cannot read properties of undefined (reading 'length')` —, as duas porque a
 * API omitia um campo que o contrato declara com padrão.
 *
 * Se a divergência for real, o erro aparece AQUI, na fronteira, com o nome do
 * campo — e não vinte componentes adiante, como uma tela de erro sem pista.
 */
export async function getListing(slugOrCode: string): Promise<Listing> {
  const resposta = await request<unknown>(`/listings/${encodeURIComponent(slugOrCode)}`, acervo);
  return listingSchema.parse(resposta);
}

export async function getFeaturedListings(): Promise<ListingCard[]> {
  return request<ListingCard[]>('/listings/featured', acervo);
}

export async function getRecentListings(): Promise<ListingCard[]> {
  return request<ListingCard[]>('/listings/recent', acervo);
}

export type FeaturedCity = { value: string; label: string; state: string; listingCount: number };

export async function getFeaturedCities(): Promise<FeaturedCity[]> {
  return request<FeaturedCity[]>('/cities/featured', acervo);
}

/**
 * Registra o interesse do comprador (Cap. 19).
 *
 * Vai para a API do acervo, que grava o contato e incrementa o contador do
 * anuncio na MESMA transacao — o numero que o anunciante olha para decidir se
 * renova o plano precisa bater com a lista de contatos que ele recebeu.
 *
 * O CRM (Cap. 13) consome o evento `lead.registered`; ainda nao existe.
 */
export type LeadConfirmation = {
  id: string;
  listingCode: string;
  /** Quem vai retornar. É a única coisa que o comprador precisa saber. */
  advertiserName: string;
};

export async function createLead(payload: {
  listingId: string;
  name: string;
  phone: string;
  email?: string | null;
  message?: string;
  source: string;
  noticeVersion?: string;
}): Promise<LeadConfirmation> {
  return request('/leads', { method: 'POST', ...acervo, body: payload });
}
